import { store } from '@/store/store'
import { originalApi } from '@/store/api/originalApi'
import { selectIsAuthenticated } from '@/store/slices/authSlice'
import {
	addRoulettePendingChip,
	aggregateRouletteChips,
	applyRouletteBetRound,
	applyRouletteSettledRound,
	clearRoulettePendingAfterRound,
	clearRoulettePendingChips,
	dismissRouletteResult,
	scaleRoulettePendingChips,
	selectRouletteGame,
	setRouletteHoveredPlace,
	setRouletteSelectedChipIndex,
	setRouletteSubmitting,
	setRouletteWheelSpinning,
	showRouletteResult,
	undoRoulettePendingChip,
} from '@/store/slices/rouletteSlice'
import { showApiErrorToast } from '@/lib/api-toast'
import { extractApiErrorMessage } from '@/lib/api-message'
import { createIdempotencyKey } from '@/lib/idempotency-key'
import { ROULETTE_CHIP_REAL_VALUES } from '@/config/original'
import type { RouletteRoundState } from '@/types/roulette'
import { SoundManager } from './SoundManager'

const RESULT_MODAL_MS = 3000
const SETTLE_HOLD_MS = 800

function wait(ms: number) {
	return new Promise<void>((resolve) => {
		window.setTimeout(resolve, ms)
	})
}

export class GameController {
	private disposed = false
	private settleStarted = false

	constructor() {
		void this.loadActiveRound()
	}

	dispose() {
		this.disposed = true
		SoundManager.instance.stopAll()
	}

	private get rouletteState() {
		return selectRouletteGame(store.getState())
	}

	private get authenticated() {
		return selectIsAuthenticated(store.getState())
	}

	private requireAuth(message: string) {
		if (this.authenticated) {
			return true
		}

		showApiErrorToast(message)
		return false
	}

	private get betsLocked() {
		const { isSubmitting, isWheelSpinning, phase } = this.rouletteState
		return isSubmitting || isWheelSpinning || phase !== 'ready'
	}

	private beginBetSpin(data: RouletteRoundState) {
		if (typeof data.winningNumber !== 'number' || data.winningNumber < 0) {
			store.dispatch(setRouletteSubmitting(false))
			showApiErrorToast('Unable to start the roulette round.')
			return
		}

		this.settleStarted = false
		store.dispatch(applyRouletteBetRound(data))
		store.dispatch(setRouletteWheelSpinning(true))
	}

	selectChip(chipIndex: number) {
		if (!this.requireAuth('Sign in to select a chip.') || this.betsLocked) {
			return
		}
		store.dispatch(setRouletteSelectedChipIndex(chipIndex))
	}

	placeBet(place: string | number) {
		if (!this.requireAuth('Sign in to place chips on the table.') || this.betsLocked) {
			return
		}

		const chipIndex = this.rouletteState.selectedChipIndex
		const amount = ROULETTE_CHIP_REAL_VALUES[chipIndex] ?? 0
		if (!Number.isFinite(amount) || amount <= 0) {
			showApiErrorToast('Select a valid chip.')
			return
		}

		store.dispatch(addRoulettePendingChip({ place: String(place), chipIndex }))
		SoundManager.instance.play('chipPlace')
	}

	undoBet() {
		if (!this.requireAuth('Sign in to edit bets.') || this.betsLocked) {
			return
		}
		store.dispatch(undoRoulettePendingChip())
	}

	clearBets() {
		if (!this.requireAuth('Sign in to edit bets.') || this.betsLocked) {
			return
		}
		store.dispatch(clearRoulettePendingChips())
	}

	scaleBets(factor: number) {
		if (!this.requireAuth('Sign in to edit bets.') || this.betsLocked || this.rouletteState.pendingChips.length === 0) {
			return
		}
		store.dispatch(scaleRoulettePendingChips(factor))
	}

	setHoveredPlace(place: string | number | null) {
		store.dispatch(setRouletteHoveredPlace(place === null ? null : String(place)))
	}

	private async loadActiveRound() {
		if (!this.authenticated || this.disposed) {
			return
		}

		try {
			const response = await store.dispatch(originalApi.endpoints.getRouletteActiveRound.initiate(undefined, { subscribe: false, forceRefetch: true })).unwrap()

			if (this.disposed) {
				return
			}

			if (response.data?.status === 'bet') {
				this.beginBetSpin(response.data)
			}
		} catch {
			// Active round is optional on first load.
		}
	}

	async startGame() {
		if (this.betsLocked) {
			return
		}

		if (!this.requireAuth('Sign in to place a bet.')) {
			return
		}

		const bets = aggregateRouletteChips(this.rouletteState.pendingChips)
		if (!bets.length) {
			showApiErrorToast('Place at least one bet on the table.')
			return
		}

		store.dispatch(setRouletteSubmitting(true))

		try {
			const response = await store
				.dispatch(
					originalApi.endpoints.startRouletteRound.initiate({
						currency_code: this.rouletteState.currencyCode,
						bets,
						idempotency_key: createIdempotencyKey(),
					}),
				)
				.unwrap()

			if (this.disposed) {
				return
			}

			this.beginBetSpin(response.data)
		} catch (error) {
			if (this.disposed) {
				return
			}

			store.dispatch(setRouletteSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to start the roulette round.')
		}
	}

	async onWheelComplete() {
		if (this.disposed || this.settleStarted) {
			return
		}
		this.settleStarted = true

		store.dispatch(setRouletteWheelSpinning(false))
		store.dispatch(setRouletteSubmitting(true))

		try {
			const response = await store.dispatch(originalApi.endpoints.settleRouletteRound.initiate()).unwrap()

			if (this.disposed) {
				return
			}

			store.dispatch(applyRouletteSettledRound(response.data))

			const isWin = response.data.status === 'win' || response.data.profit > 0
			SoundManager.instance.play(isWin ? 'roundWin' : 'roundLoss')
			store.dispatch(showRouletteResult())

			await wait(RESULT_MODAL_MS)
			if (this.disposed) {
				return
			}

			store.dispatch(dismissRouletteResult())
			await wait(SETTLE_HOLD_MS)
			if (this.disposed) {
				return
			}

			store.dispatch(clearRoulettePendingAfterRound())
		} catch (error) {
			if (this.disposed) {
				return
			}

			this.settleStarted = false
			store.dispatch(setRouletteSubmitting(false))
			showApiErrorToast(extractApiErrorMessage(error) ?? 'Unable to settle the roulette round.')
		}
	}
}
